import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  RefreshCw, Zap, Clock, Shield, AlertTriangle, CheckCircle,
  XCircle, Database, Hash, Eye, Film, Image as ImageIcon,
  ChevronDown, ChevronRight, Lock, Loader, Info, Layers,
  GitBranch, ArrowRightLeft, Trash2, AlertOctagon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Diagnostic state config ─────────────────────────────────────────────
const STATE_CONFIG = {
  usable_cache:           { label: 'Usable Cache',          color: '#10b981', icon: CheckCircle },
  cache_hit_active:       { label: 'Cache Hit',             color: '#06b6d4', icon: ArrowRightLeft },
  invalidated_source:     { label: 'Invalidated Source',    color: '#f97316', icon: AlertOctagon },
  invalidated_cache_hit:  { label: 'Invalidated Cache Hit', color: '#f59e0b', icon: AlertOctagon },
  expired_cleanable:      { label: 'Expired (Cleanable)',   color: '#f59e0b', icon: Clock },
  expired:                { label: 'Expired',               color: '#f59e0b', icon: Clock },
  blocked_shared_output:  { label: 'Blocked Shared',        color: '#ef4444', icon: Lock },
  stale_no_hash:          { label: 'Stale (No Hash)',       color: '#a78bfa', icon: Hash },
  stale_hash_mismatch:    { label: 'Stale (Hash Mismatch)', color: '#a78bfa', icon: Hash },
  orphaned_slide_deleted: { label: 'Orphaned (Slide)',      color: '#f97316', icon: AlertTriangle },
  orphaned_carousel_deleted: { label: 'Orphaned (Carousel)', color: '#f97316', icon: AlertTriangle },
  anonymous_expired:      { label: 'Anonymous Expired',     color: '#f59e0b', icon: Clock },
  invalid:                { label: 'Invalid',               color: '#dc2626', icon: XCircle },
  deleted:                { label: 'Deleted',               color: '#525252', icon: Trash2 },
  shared_output:          { label: 'Shared Output',         color: '#3b82f6', icon: Database },
  in_progress:            { label: 'In Progress',           color: '#06b6d4', icon: Loader },
  failed:                 { label: 'Failed',                color: '#dc2626', icon: XCircle },
  cancelled:              { label: 'Cancelled',             color: '#737373', icon: XCircle },
  no_output:              { label: 'No Output',             color: '#737373', icon: Info },
  unknown:                { label: 'Unknown',               color: '#737373', icon: Info },
};

const ROLE_CONFIG = {
  source_output:  { label: 'Source Output',  color: '#10b981', icon: Database },
  cache_hit:      { label: 'Cache Hit',      color: '#06b6d4', icon: ArrowRightLeft },
  legacy_no_hash: { label: 'Legacy / No Hash', color: '#a78bfa', icon: Hash },
  failed:         { label: 'Failed',         color: '#dc2626', icon: XCircle },
  deleted:        { label: 'Deleted',        color: '#525252', icon: Trash2 },
};

// ── Plain-english guidance per state ────────────────────────────────────
const STATE_GUIDANCE = {
  usable_cache: 'Keep — this is a valid cache source. Reusable for matching render requests.',
  cache_hit_active: 'Keep — audit/history row that reuses another job\'s S3 output. Does NOT own the physical object.',
  invalidated_source: 'Cleanup through staged cleanup (not via diagnostics). S3 object can be deleted once grace period passes and no active sharers remain.',
  invalidated_cache_hit: 'Cleanup at row level only — mark output_deleted_at. The physical S3 object belongs to the source render.',
  expired_cleanable: 'Cleanup through staged cleanup. Past TTL with no active sharers.',
  blocked_shared_output: 'DO NOT delete manually — output_key is shared with an active, non-invalidated render. Cleanup must mark this row only, never the S3 object.',
  stale_no_hash: 'Old pre-cache render. Not reusable as a cache entry. Will expire by TTL.',
  stale_hash_mismatch: 'Render hash differs from the latest for this slide. Content may have changed.',
  orphaned_carousel_deleted: 'Source carousel is gone. Cleanup candidate after grace period.',
  orphaned_slide_deleted: 'Source slide is gone from carousel. Cleanup candidate after grace period.',
  anonymous_expired: 'Anonymous render past TTL. Cleanup candidate.',
  invalid: 'S3 object failed validation. Cleanup candidate (unless shared).',
  deleted: 'Historical record only. output_deleted_at is set.',
  in_progress: 'Render still in progress. Wait for completion.',
  failed: 'Render failed. Partial/no output. Cleanup candidate if no sharers.',
  cancelled: 'Render cancelled. No output to clean.',
  no_output: 'No output_key. Nothing to clean.',
  shared_output: 'Active output_key shared by 2+ active jobs.',
  unknown: 'Unclassified — please report.',
};

// ── Cleanup eligibility label ───────────────────────────────────────────
function getCleanupLabel(item) {
  if (item.output_deleted_at) return { text: 'Deleted', color: '#525252' };
  if (item.diagnostic_state === 'in_progress') return { text: 'In Progress', color: '#06b6d4' };
  if (item.diagnostic_state === 'failed' || item.diagnostic_state === 'cancelled') {
    return { text: item.cleanup_candidate ? 'Eligible' : 'Not Eligible', color: item.cleanup_candidate ? '#10b981' : '#525252' };
  }
  if (item.diagnostic_state === 'blocked_shared_output') {
    return { text: 'Blocked Shared', color: '#ef4444' };
  }
  if (item.diagnostic_state === 'stale_no_hash') return { text: 'Stale No Hash', color: '#a78bfa' };
  if (item.cleanup_candidate) return { text: 'Eligible', color: '#f59e0b' };
  return { text: 'Not Eligible', color: '#525252' };
}

function StateBadge({ state }) {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.unknown;
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`state-badge-${state}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: `${cfg.color}22`, color: cfg.color,
        border: `1px solid ${cfg.color}44`,
        borderRadius: 4, padding: '2px 8px', fontSize: 11,
        fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace",
      }}
    >
      <Icon size={12} />
      {cfg.label}
    </span>
  );
}

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || { label: role || 'unknown', color: '#737373', icon: Info };
  const Icon = cfg.icon;
  return (
    <span
      data-testid={`role-badge-${role}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: `${cfg.color}1a`, color: cfg.color,
        border: `1px dashed ${cfg.color}55`,
        borderRadius: 4, padding: '2px 8px', fontSize: 10,
        fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace",
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}
    >
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

function SummaryCard({ icon, label, value, color, subtitle, testId, badges }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: '#1a1a1a', border: '1px solid #333', borderRadius: 8,
        padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#a3a3a3', fontSize: 11 }}>
        {icon}
        {label}
      </div>
      <div style={{ color: color || '#e5e5e5', fontSize: 22, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ color: '#737373', fontSize: 10 }}>{subtitle}</div>
      )}
      {badges && badges.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {badges.map((b, i) => (
            <span key={i} style={{
              fontSize: 10, fontFamily: "'IBM Plex Mono', monospace",
              color: b.color || '#a3a3a3',
              background: `${b.color || '#333'}1a`,
              padding: '1px 6px', borderRadius: 3,
            }}>
              {b.label}: {b.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CacheRelationshipBlock({ rel }) {
  if (!rel) return null;
  return (
    <div data-testid="cache-relationship-block" style={styles.cacheRelBox}>
      <div style={styles.cacheRelHeader}>
        <GitBranch size={12} />
        <span>Cache Relationship</span>
      </div>
      <div style={styles.cacheRelGrid}>
        <DetailField label="Role" value={rel.is_source_output ? 'Source Output (owns S3 object)' : (rel.is_cache_hit ? 'Cache Hit (does NOT own S3 object)' : 'Other')} />
        {rel.is_cache_hit && (
          <>
            <DetailField label="Source Job ID" value={rel.source_job_id || '—'} mono />
            <DetailField label="Source Status" value={rel.source_status || '—'} />
            <DetailField label="Source Invalidated" value={rel.source_invalidated ? 'Yes' : 'No'} />
            <DetailField label="Source Deleted" value={rel.source_deleted ? 'Yes' : 'No'} />
          </>
        )}
        <DetailField label="Group Size" value={rel.shared_output_group_size} />
        <DetailField label="Active Non-Invalidated Sharers" value={rel.active_non_invalidated_sharers} />
        <DetailField label="Invalidated Sharers" value={rel.invalidated_sharers} />
        <DetailField label="Deleted Sharers" value={rel.deleted_sharers} />
      </div>

      <div style={{
        marginTop: 10, padding: '8px 12px',
        background: rel.can_delete_s3_object ? '#14532d22' : '#7f1d1d22',
        border: `1px solid ${rel.can_delete_s3_object ? '#14532d88' : '#7f1d1d88'}`,
        borderRadius: 6, fontSize: 11,
        color: rel.can_delete_s3_object ? '#86efac' : '#fca5a5',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {rel.can_delete_s3_object
          ? <CheckCircle size={14} />
          : <Lock size={14} />}
        <span>
          S3 physical deletion:{' '}
          <strong>{rel.can_delete_s3_object ? 'allowed' : 'blocked'}</strong>
          {rel.blocking_reason && ` — ${rel.blocking_reason}`}
        </span>
      </div>

      {rel.sharers && rel.sharers.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: '#a3a3a3', fontSize: 10, textTransform: 'uppercase', marginBottom: 6 }}>
            Sharer rows ({rel.sharers.length}{rel.sharers_truncated ? '+' : ''})
          </div>
          <div style={styles.sharerList}>
            {rel.sharers.map((s, i) => (
              <div key={i} style={{
                ...styles.sharerRow,
                borderLeftColor: s.role_in_group === 'active' ? '#10b981'
                  : s.role_in_group === 'invalidated' ? '#f97316'
                  : s.role_in_group === 'deleted' ? '#525252' : '#737373',
              }}>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                  {(s.job_id || '').slice(0, 16)}…
                </span>
                <span style={{ color: '#a3a3a3', fontSize: 11 }}>{s.status}</span>
                <span style={styles.sharerRole}>{s.role_in_group}</span>
                {s.is_cache_hit && <span style={{ ...styles.sharerRole, color: '#06b6d4', borderColor: '#06b6d488' }}>cache_hit</span>}
                {s.invalidated && <span style={{ ...styles.sharerRole, color: '#f97316', borderColor: '#f9731688' }}>invalidated</span>}
                {s.deleted && <span style={{ ...styles.sharerRole, color: '#525252', borderColor: '#52525288' }}>deleted</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GuidancePanel({ state }) {
  const text = STATE_GUIDANCE[state] || STATE_GUIDANCE.unknown;
  return (
    <div data-testid="guidance-panel" style={styles.guidanceBox}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#a78bfa', fontWeight: 600, fontSize: 11 }}>
        <Info size={12} /> What should I do?
      </span>
      <span style={{ color: '#d4d4d4', fontSize: 12 }}>{text}</span>
    </div>
  );
}

export default function AdminRenderDiagnostics() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_password') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterState, setFilterState] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterKind, setFilterKind] = useState('');
  const [includeS3Check, setIncludeS3Check] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const headers = { 'X-Admin-Password': password };

  const fetchSummary = useCallback(async () => {
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/admin/operations/render-diagnostics/summary`,
        { headers }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data.error) {
          setError(data.detail || data.error);
          return;
        }
        setSummary(data);
      }
    } catch (e) {
      console.error('[render-diagnostics] summary error:', e);
    }
  }, [password]);

  const fetchDetails = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (filterState) params.set('state', filterState);
      if (filterRole) params.set('role', filterRole);
      if (filterKind) params.set('kind', filterKind);
      if (includeS3Check) params.set('include_s3_check', 'true');

      const resp = await fetch(
        `${BACKEND_URL}/api/admin/operations/render-diagnostics?${params}`,
        { headers }
      );
      if (resp.ok) {
        const data = await resp.json();
        setDetails(data);
      }
    } catch (e) {
      console.error('[render-diagnostics] details error:', e);
    }
  }, [password, filterState, filterRole, filterKind, includeS3Check]);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${BACKEND_URL}/api/admin/operations/render-diagnostics/summary`,
        { headers: { 'X-Admin-Password': password } }
      );
      if (resp.ok) {
        sessionStorage.setItem('admin_password', password);
        setAuthenticated(true);
      } else {
        setError('Invalid password');
      }
    } catch (e) {
      setError('Connection failed');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (password && !authenticated) {
      handleLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchSummary();
      fetchDetails();
    }
  }, [authenticated, fetchSummary, fetchDetails]);

  const refresh = async () => {
    setLoading(true);
    await Promise.all([fetchSummary(), fetchDetails()]);
    setLoading(false);
  };

  const toggleRow = (jobId) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  // ── Login screen ────────────────────────────────────────────────────
  if (!authenticated) {
    return (
      <AdminLayout>
        <div style={styles.loginContainer}>
          <h2 style={styles.loginTitle}>
            <Zap size={20} color="#8b5cf6" /> Render Output Diagnostics
          </h2>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            placeholder="Admin Password"
            style={styles.loginInput}
          />
          <button
            data-testid="admin-login-btn"
            onClick={handleLogin}
            style={styles.loginBtn}
            disabled={loading}
          >
            {loading ? 'Checking...' : 'Login'}
          </button>
          {error && <div style={styles.errorText}>{error}</div>}
        </div>
      </AdminLayout>
    );
  }

  const s = summary?.summary || {};
  const bs = s.blocked_shared_breakdown || { expired: 0, invalidated: 0, total: 0 };
  const totalJobs = summary?.total_render_jobs || 0;

  return (
    <AdminLayout>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>
              <Zap size={22} color="#8b5cf6" style={{ marginRight: 8 }} />
              Render Output Diagnostics
            </h1>
            <p style={styles.subtitle}>
              Cache validity, shared output safety, and cleanup eligibility.
              Read-only — no destructive operations.
            </p>
          </div>
          <button
            data-testid="refresh-btn"
            onClick={refresh}
            style={styles.refreshBtn}
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
            Refresh
          </button>
        </div>

        {error && (
          <div style={styles.errorBanner}>
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {/* Summary Cards */}
        {summary && (
          <div style={styles.summaryGrid}>
            <SummaryCard
              testId="summary-total"
              icon={<Database size={14} />}
              label="Total Render Jobs"
              value={totalJobs}
              color="#e5e5e5"
            />
            <SummaryCard
              testId="summary-usable-cache"
              icon={<CheckCircle size={14} />}
              label="Usable Cache"
              value={s.usable_cache || 0}
              color="#10b981"
              subtitle="Active source outputs with render_hash"
            />
            <SummaryCard
              testId="summary-cache-hit-active"
              icon={<ArrowRightLeft size={14} />}
              label="Cache Hits (Active)"
              value={s.cache_hit_active || 0}
              color="#06b6d4"
              subtitle="Audit/history rows reusing another output"
            />
            <SummaryCard
              testId="summary-source-output"
              icon={<Database size={14} />}
              label="Source Outputs"
              value={s.source_output_count || 0}
              color="#10b981"
              subtitle="Active rows that own their S3 object"
            />
            <SummaryCard
              testId="summary-invalidated"
              icon={<AlertOctagon size={14} />}
              label="Invalidated"
              value={s.invalidated_total || 0}
              color="#f97316"
              subtitle="Marked stale by slide/carousel events"
              badges={[
                { label: 'source', value: s.invalidated_source || 0, color: '#f97316' },
                { label: 'cache-hit', value: s.invalidated_cache_hit || 0, color: '#f59e0b' },
              ]}
            />
            <SummaryCard
              testId="summary-blocked-shared"
              icon={<Lock size={14} />}
              label="Blocked Shared"
              value={bs.total || 0}
              color="#ef4444"
              subtitle="Cannot delete S3 — active sharer protects it"
              badges={(bs.total || 0) > 0 ? [
                { label: 'expired', value: bs.expired, color: '#f59e0b' },
                { label: 'invalidated', value: bs.invalidated, color: '#f97316' },
              ] : []}
            />
            <SummaryCard
              testId="summary-expired-cleanable"
              icon={<Clock size={14} />}
              label="Expired Cleanable"
              value={s.expired_cleanable || 0}
              color="#f59e0b"
              subtitle="Past TTL, no active sharers"
            />
            <SummaryCard
              testId="summary-stale-no-hash"
              icon={<Hash size={14} />}
              label="Stale (No Hash)"
              value={s.stale_no_hash || 0}
              color="#a78bfa"
              subtitle="Pre-cache renders, not reusable"
            />
            <SummaryCard
              testId="summary-cleanup-eligible"
              icon={<Trash2 size={14} />}
              label="Cleanup Eligible"
              value={s.cleanup_eligible || 0}
              color="#f59e0b"
              subtitle="Run through staged cleanup, not here"
            />
            <SummaryCard
              testId="summary-cleanup-blocked"
              icon={<Lock size={14} />}
              label="Cleanup Blocked"
              value={s.cleanup_blocked || 0}
              color="#ef4444"
              subtitle="Shared output protection active"
            />
            <SummaryCard
              testId="summary-deleted"
              icon={<XCircle size={14} />}
              label="Deleted"
              value={s.deleted || 0}
              color="#525252"
              subtitle="Historical (output_deleted_at set)"
            />
            <SummaryCard
              testId="summary-shared-groups"
              icon={<Layers size={14} />}
              label="Shared Output Groups"
              value={s.shared_output_groups || 0}
              color="#3b82f6"
              subtitle="output_keys shared by 2+ active rows"
            />
          </div>
        )}

        {/* Config Info */}
        {summary && (
          <div style={styles.configBar} data-testid="config-bar">
            <span>
              <Zap size={12} style={{ marginRight: 4 }} />
              Cache: {summary.render_cache_enabled ? 'Enabled' : 'Disabled'}
            </span>
            <span>Profile v{summary.render_profile_version}</span>
            <span>source_job_id: {summary.has_source_job_id_column ? 'yes' : 'no (v011 needed)'}</span>
            <span>invalidation: {summary.has_invalidation_columns ? 'yes' : 'no'}</span>
            {summary.kind_breakdown && Object.entries(summary.kind_breakdown).map(([k, v]) => (
              <span key={k}>
                {k === 'image' ? <ImageIcon size={12} style={{ marginRight: 4 }} /> : <Film size={12} style={{ marginRight: 4 }} />}
                {k}: {v}
              </span>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={styles.filterBar} data-testid="filter-bar">
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>State</label>
            <select
              data-testid="filter-state"
              value={filterState}
              onChange={e => setFilterState(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All States</option>
              <option value="usable_cache">Usable Cache</option>
              <option value="cache_hit_active">Cache Hit (Active)</option>
              <option value="invalidated_source">Invalidated Source</option>
              <option value="invalidated_cache_hit">Invalidated Cache Hit</option>
              <option value="cleanup_candidate">Cleanup Candidate</option>
              <option value="expired_cleanable">Expired Cleanable</option>
              <option value="blocked_shared_output">Blocked Shared</option>
              <option value="stale_no_hash">Stale (No Hash)</option>
              <option value="stale_hash_mismatch">Stale (Hash Mismatch)</option>
              <option value="orphaned_carousel_deleted">Orphaned Carousel</option>
              <option value="orphaned_slide_deleted">Orphaned Slide</option>
              <option value="deleted">Deleted</option>
              <option value="failed">Failed</option>
              <option value="in_progress">In Progress</option>
              <option value="invalid">Invalid</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Role</label>
            <select
              data-testid="filter-role"
              value={filterRole}
              onChange={e => setFilterRole(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Roles</option>
              <option value="source_output">Source Output</option>
              <option value="cache_hit">Cache Hit</option>
              <option value="legacy_no_hash">Legacy / No Hash</option>
              <option value="failed">Failed</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Kind</label>
            <select
              data-testid="filter-kind"
              value={filterKind}
              onChange={e => setFilterKind(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </div>
          <label style={styles.checkboxLabel}>
            <input
              data-testid="filter-s3-check"
              type="checkbox"
              checked={includeS3Check}
              onChange={e => setIncludeS3Check(e.target.checked)}
            />
            S3 validation (slow, max 50)
          </label>
          <button data-testid="apply-filters-btn" onClick={fetchDetails} style={styles.applyBtn}>
            <Eye size={12} /> Apply
          </button>
        </div>

        {/* Details Table */}
        {details && (
          <div style={styles.tableContainer} data-testid="diagnostics-table">
            <div style={styles.tableHeader}>
              <span style={styles.tableTitle}>
                Render Outputs ({details.returned} of {details.total_matching})
              </span>
              {details.s3_checks_performed > 0 && (
                <span style={styles.s3Badge}>
                  <Shield size={10} /> S3 checks: {details.s3_checks_performed}
                </span>
              )}
            </div>

            {details.items.length === 0 ? (
              <div style={styles.emptyState} data-testid="empty-state">
                No render outputs match the current filters.
              </div>
            ) : (
              <div style={styles.tableScroll}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}></th>
                      <th style={styles.th}>Job ID</th>
                      <th style={styles.th}>Kind</th>
                      <th style={styles.th}>Role</th>
                      <th style={styles.th}>State</th>
                      <th style={styles.th}>Sharers</th>
                      <th style={styles.th}>Cleanup</th>
                      <th style={styles.th}>S3 Deletable</th>
                      <th style={styles.th}>Expires</th>
                      <th style={styles.th}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.items.map(item => {
                      const cu = getCleanupLabel(item);
                      const rel = item.cache_relationship || {};
                      const expanded = expandedRows.has(item.job_id);
                      return (
                        <React.Fragment key={item.job_id}>
                          <tr
                            data-testid={`row-${item.job_id}`}
                            style={{
                              ...styles.tr,
                              cursor: 'pointer',
                              background: expanded ? '#1f1f1f' : undefined,
                            }}
                            onClick={() => toggleRow(item.job_id)}
                          >
                            <td style={styles.td}>
                              {expanded
                                ? <ChevronDown size={14} color="#737373" />
                                : <ChevronRight size={14} color="#737373" />}
                            </td>
                            <td style={{ ...styles.td, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                              {item.job_id?.slice(0, 14)}…
                            </td>
                            <td style={styles.td}>
                              {item.kind === 'image'
                                ? <ImageIcon size={12} color="#06b6d4" />
                                : <Film size={12} color="#ec4899" />}
                              {' '}{item.kind}
                            </td>
                            <td style={styles.td}>
                              <RoleBadge role={item.role} />
                            </td>
                            <td style={styles.td}>
                              <StateBadge state={item.diagnostic_state} />
                            </td>
                            <td style={styles.td}>
                              <span style={{
                                color: item.active_sharers_count > 0 ? '#ef4444' : '#525252',
                                fontWeight: item.active_sharers_count > 0 ? 700 : 400,
                              }}>
                                {item.active_sharers_count}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <span style={{ color: cu.color, fontSize: 11, fontWeight: 600 }}>
                                {cu.text}
                              </span>
                            </td>
                            <td style={styles.td}>
                              {rel.can_delete_s3_object ? (
                                <span style={{ color: '#10b981', fontSize: 11, fontWeight: 600 }}>Allowed</span>
                              ) : (
                                <span style={{ color: '#ef4444', fontSize: 11, fontWeight: 600 }}>Blocked</span>
                              )}
                            </td>
                            <td style={{ ...styles.td, fontSize: 11, color: '#a3a3a3' }}>
                              {item.output_expires_at
                                ? new Date(item.output_expires_at).toLocaleDateString()
                                : '—'}
                            </td>
                            <td style={{ ...styles.td, fontSize: 11, color: '#a3a3a3' }}>
                              {item.created_at
                                ? new Date(item.created_at).toLocaleDateString()
                                : '—'}
                            </td>
                          </tr>

                          {/* Expanded detail row */}
                          {expanded && (
                            <tr>
                              <td colSpan={10} style={{ padding: 0, border: 'none' }}>
                                <div style={styles.expandedPanel} data-testid={`expanded-${item.job_id}`}>
                                  <div style={styles.expandedGrid}>
                                    <DetailField label="Job ID" value={item.job_id} mono />
                                    <DetailField label="Output Key" value={item.output_key || '—'} mono />
                                    <DetailField label="Render Hash" value={item.render_hash || '—'} mono />
                                    <DetailField label="Source Job" value={item.source_job_id || '—'} mono />
                                    <DetailField label="Carousel ID" value={item.carousel_id || '—'} mono />
                                    <DetailField label="Slide ID" value={item.slide_id || '—'} mono />
                                    <DetailField label="User ID" value={item.user_id || '—'} mono />
                                    <DetailField label="Output Deleted At" value={item.output_deleted_at || '—'} />
                                    <DetailField label="Output Invalidated At" value={item.output_invalidated_at || '—'} />
                                    <DetailField
                                      label="Invalidation Reason"
                                      value={item.output_invalidated_reason || '—'}
                                    />
                                  </div>

                                  {/* Cache relationship */}
                                  <CacheRelationshipBlock rel={item.cache_relationship} />

                                  {/* Guidance panel */}
                                  <GuidancePanel state={item.diagnostic_state} />

                                  {/* Recommendation */}
                                  <div style={styles.recommendation}>
                                    <Info size={12} style={{ marginRight: 6, flexShrink: 0 }} />
                                    {item.recommendation}
                                  </div>

                                  {/* Blockers */}
                                  {item.blockers && item.blockers.length > 0 && (
                                    <div style={styles.blockersList}>
                                      <span style={styles.blockersLabel}>
                                        <Shield size={12} /> Blockers:
                                      </span>
                                      {item.blockers.map((b, i) => (
                                        <span key={i} style={styles.blockerItem}>{b}</span>
                                      ))}
                                    </div>
                                  )}

                                  {/* S3 Validation */}
                                  {item.s3_validation && (
                                    <div style={styles.s3ValidationBox}>
                                      <span style={styles.s3ValidationTitle}>
                                        <Shield size={12} /> S3 Validation
                                      </span>
                                      <span>Exists: {item.s3_validation.exists ? 'yes' : 'no'}</span>
                                      <span>Size: {item.s3_validation.size_bytes != null ? `${(item.s3_validation.size_bytes / 1024).toFixed(1)} KB` : '—'}</span>
                                      <span>Type: {item.s3_validation.content_type || '—'}</span>
                                      <span>Valid: {item.s3_validation.valid ? 'yes' : 'no'}</span>
                                      {item.s3_validation.issues && item.s3_validation.issues.length > 0 && (
                                        <span style={{ color: '#ef4444' }}>
                                          Issues: {item.s3_validation.issues.join('; ')}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ color: '#737373', fontSize: 10, textTransform: 'uppercase' }}>{label}</span>
      <span style={{
        color: '#d4d4d4', fontSize: 12,
        fontFamily: mono ? "'IBM Plex Mono', monospace" : 'inherit',
        wordBreak: 'break-all',
      }}>
        {value}
      </span>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
const styles = {
  container: { maxWidth: 1320, margin: '0 auto', padding: '24px 16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { color: '#e5e5e5', fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center' },
  subtitle: { color: '#737373', fontSize: 12, margin: '6px 0 0' },
  refreshBtn: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
    background: '#262626', color: '#d4d4d4', border: '1px solid #404040',
    borderRadius: 6, cursor: 'pointer', fontSize: 12,
  },
  errorBanner: {
    background: '#7f1d1d', color: '#fca5a5', padding: '8px 14px',
    borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center',
    gap: 6, marginBottom: 16,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12, marginBottom: 20,
  },
  configBar: {
    display: 'flex', flexWrap: 'wrap', gap: 16, padding: '10px 14px',
    background: '#1a1a1a', border: '1px solid #333', borderRadius: 6,
    marginBottom: 16, fontSize: 11, color: '#a3a3a3', alignItems: 'center',
  },
  filterBar: {
    display: 'flex', flexWrap: 'wrap', gap: 12, padding: '10px 14px',
    background: '#1a1a1a', border: '1px solid #333', borderRadius: 6,
    marginBottom: 16, alignItems: 'center',
  },
  filterGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  filterLabel: { color: '#a3a3a3', fontSize: 11, fontWeight: 600 },
  filterSelect: {
    background: '#262626', color: '#d4d4d4', border: '1px solid #404040',
    borderRadius: 4, padding: '4px 8px', fontSize: 12,
  },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: 6, color: '#a3a3a3', fontSize: 11, cursor: 'pointer' },
  applyBtn: {
    display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
    background: '#8b5cf6', color: 'white', border: 'none', borderRadius: 4,
    cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  tableContainer: { background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, overflow: 'hidden' },
  tableHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', borderBottom: '1px solid #333',
  },
  tableTitle: { color: '#e5e5e5', fontSize: 13, fontWeight: 600 },
  s3Badge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: '#14532d', color: '#86efac', padding: '2px 8px',
    borderRadius: 4, fontSize: 10,
  },
  tableScroll: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '8px 12px', color: '#737373', fontSize: 10,
    textTransform: 'uppercase', fontWeight: 600, borderBottom: '1px solid #333',
    whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid #262626' },
  td: { padding: '8px 12px', color: '#d4d4d4', fontSize: 12, whiteSpace: 'nowrap' },
  emptyState: { padding: 40, textAlign: 'center', color: '#525252', fontSize: 13 },
  expandedPanel: {
    padding: '16px 24px', background: '#141414',
    borderTop: '1px solid #262626', borderBottom: '1px solid #333',
  },
  expandedGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 12, marginBottom: 12,
  },
  cacheRelBox: {
    background: '#0f1419', border: '1px solid #1f2937', borderRadius: 8,
    padding: '12px 14px', marginBottom: 12,
  },
  cacheRelHeader: {
    display: 'flex', alignItems: 'center', gap: 6, color: '#06b6d4',
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: 0.5, marginBottom: 10,
  },
  cacheRelGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 8,
  },
  sharerList: { display: 'flex', flexDirection: 'column', gap: 4 },
  sharerRow: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px',
    background: '#1a1a1a', borderLeft: '3px solid #333', borderRadius: 4,
  },
  sharerRole: {
    fontSize: 10, fontFamily: "'IBM Plex Mono', monospace",
    color: '#a3a3a3', border: '1px solid #404040', borderRadius: 3,
    padding: '1px 6px', textTransform: 'uppercase',
  },
  guidanceBox: {
    display: 'flex', flexDirection: 'column', gap: 4,
    padding: '10px 14px', background: '#1e1b4b22',
    border: '1px solid #4c1d9544', borderRadius: 6, marginBottom: 8,
  },
  recommendation: {
    display: 'flex', alignItems: 'flex-start', padding: '8px 12px',
    background: '#1a1a2e', border: '1px solid #2d2d5a', borderRadius: 6,
    color: '#a3a3c2', fontSize: 11, marginBottom: 8,
  },
  blockersList: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 8 },
  blockersLabel: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    color: '#ef4444', fontSize: 11, fontWeight: 600,
  },
  blockerItem: {
    background: '#7f1d1d', color: '#fca5a5', padding: '2px 8px',
    borderRadius: 4, fontSize: 10,
  },
  s3ValidationBox: {
    display: 'flex', flexWrap: 'wrap', gap: 12, padding: '8px 12px',
    background: '#1a2e1a', border: '1px solid #2d5a2d', borderRadius: 6,
    fontSize: 11, color: '#a3c2a3', alignItems: 'center',
  },
  s3ValidationTitle: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontWeight: 600, color: '#86efac',
  },
  loginContainer: {
    maxWidth: 400, margin: '80px auto', padding: 32,
    background: '#1a1a1a', border: '1px solid #333',
    borderRadius: 12, textAlign: 'center',
  },
  loginTitle: {
    color: '#e5e5e5', fontSize: 18, fontWeight: 700, display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16,
  },
  loginInput: {
    width: '100%', padding: '10px 14px', background: '#262626',
    color: '#d4d4d4', border: '1px solid #404040', borderRadius: 6,
    fontSize: 14, marginBottom: 12, boxSizing: 'border-box',
  },
  loginBtn: {
    width: '100%', padding: '10px 14px', background: '#8b5cf6',
    color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },
  errorText: { color: '#ef4444', fontSize: 12, marginTop: 8 },
};
